import React, { useEffect, useState } from "react";

import {
  AvatarGroup,
  AvatarGroupItem,
  AvatarGroupPopover,
  Button,
  Caption1Strong,
  CounterBadge,
  Divider,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  OverlayDrawer,
  Persona,
  SearchBox,
  makeStyles,
  partitionAvatarGroupItems
} from "@fluentui/react-components";
import {
  Dismiss24Regular,
  ChatMultiple20Filled,
  ClockFilled,
  Mic20Filled,
  MicOff20Filled,
  Settings20Filled
} from "@fluentui/react-icons";
import { FormattedMessage } from "react-intl";
import { useRole } from "./useRole";
import { useSocialContext } from "../contexts/SocialContext";
import useBreakpoints from "../hooks/useBreakpoints";
import configManager from "../../singleton/ConfigManager";

const useStyles = makeStyles({
  drawer: {
    width: "400px"
  },
  headerDrawer: {
    display: " flex",
    padding: " var(--Font-size-600, 24px) 16px 12px var(--Font-size-600, 24px)",
    alignItems: " flex-start",
    gap: " 8px",
    alignSelf: " stretch",
    backgroundColor: "#16181E"
  },
  drawerBody: {
    paddingTop: "20px"
  },
  search: {
    backgroundColor: "rgba(255, 255, 255, 0.10)",
    width: "100%"
  },
  section: {
    display: " flex",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "2px"
  },
  sectionHeader: {
    display: " flex",
    padding: " 8px 0px",
    flexDirection: " column",
    alignItems: " flex-start",
    gap: "var(--Font-size-100, 10px)",
    alignSelf: "stretch"
  },
  sectionHeaderText: {
    color: "var(--Gray-3, #828282)"
  },
  sectionContent: {
    width: "100%",
    display: " flex",
    padding: " 4px",
    alignItems: " center",
    height: "48px",
    borderRadius: "4px",
    justifyContent: "space-between",
    gap: " 8px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    }
  },
  persona: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "32px",
    "& .fui-Avatar__image": {
      width: "32px",
      height: "32px"
    },
    "& .fui-Avatar": {
      height: "32px"
    },
    "& .fui-Persona": {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "32px"
    }
  },
  icons: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    "& > *": {
      cursor: "pointer"
    }
  },
  divider: {
    padding: "5px 0px 4px 0px"
  },
  footerParticipants: {
    display: "flex",
    padding: "16px var(--Font-size-600, 24px) var(--Font-size-600, 24px) var(--Font-size-600, 24px)",
    justifyContent: "flex-end",
    width: "100%",
    backgroundColor: "#16181E"
  },
  redIcon: {
    color: "var(--Red, #EB5757)"
  },
  avatarGroupItem: {
    backgroundColor: "#333",
    "& img": {
      backgroundColor: "#333"
    }
  },
  avatarGroupItemAll: {
    "& img": {
      backgroundColor: "#333"
    }
  }
});

export default function FluentParticipantOverlay({
  people,
  hubChannel,
  isOpen,
  onMute,
  selectPerson,
  closeParticipants,
  agents
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredPeople =
    searchQuery.trim() === ""
      ? people
      : people.filter(person => person.profile.displayName.toLowerCase().includes(searchQuery.toLowerCase()));

  const items = filteredPeople.map(person => ({
    id: person?.id,
    name: person?.profile?.displayName,
    image: person?.profile?.thumbnail
  }));

  const [partitionedItems, setPartitionedItems] = useState(partitionAvatarGroupItems({ items }));

  const { isOpenChat, openChatUser, openChatUserAll } = useSocialContext();

  const styles = useStyles();

  const userOwner = filteredPeople.filter(person => person.roles.owner);
  const guestPeople = filteredPeople.filter(person => !person.roles.owner);

  const canUpdateRoles = hubChannel.canOrWillIfCreator("update_roles");
  const isMod = useRole("owner");

  const canUpdateAllSettings = isMod;

  const breakpoint = useBreakpoints();
  const isMobile = breakpoint === "sm" || breakpoint === "xs";

  useEffect(() => {
    setPartitionedItems(partitionAvatarGroupItems({ items }));
  }, [filteredPeople]);

  const textChatEnabled = !!configManager.config.textChat?.enabled;

  return (
    <OverlayDrawer
      size={"full"}
      className={!isMobile ? styles.drawer : {}}
      modalType="non-modal"
      open={isOpen}
      // onOpenChange={(_, { open }) => setIsOpen(open)}
    >
      <DrawerHeader className={styles.headerDrawer}>
        <DrawerHeaderTitle
          style={{ width: "100%" }}
          action={
            <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={closeParticipants} />
          }
        >
          <FormattedMessage
            id="people-sidebar.title"
            defaultMessage="Participants ({numPeople})"
            values={{ numPeople: people.length }}
          />
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody} style={isOpenChat ? { maxHeight: "calc(50vh - 70px)" } : {}}>
        <SearchBox
          onChange={e => setSearchQuery(e?.target?.value || "")}
          placeholder="Search"
          appearance="filled-lighter"
          className={styles.search}
        />
        <div className={styles.contentParticipants}>
          {/* Not users found*/}
          {filteredPeople.length === 0 && (
            <section className={styles.sectionNotFound}>
              <Caption1Strong className={styles.sectionHeaderText}>No users found</Caption1Strong>
            </section>
          )}

          {/* AI Agents Section*/}
          {agents.length !== 0 && (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <Caption1Strong className={styles.sectionHeaderText}>AI Agent</Caption1Strong>
              </div>

              {agents.map((agent, index) => {
                return (
                  <AIParticipant
                    key={`ai-participant-${index}`}
                    agent={agent}
                    openChatUser={openChatUser}
                    styles={styles}
                  />
                );
              })}
            </section>
          )}

          {/* All Users Section*/}
          {filteredPeople.length !== 0 && (
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <Caption1Strong className={styles.sectionHeaderText}>All Users</Caption1Strong>
              </div>
              <div className={styles.sectionContent}>
                <AvatarGroup layout="stack">
                  {partitionedItems.inlineItems && (
                    <>
                      {partitionedItems.inlineItems.map(item => (
                        <AvatarGroupItem
                          className={styles.avatarGroupItem}
                          key={item?.id}
                          image={{ src: item.image }}
                          color={"anchor"}
                        />
                      ))}
                    </>
                  )}

                  {partitionedItems.overflowItems && (
                    <AvatarGroupPopover>
                      {partitionedItems.overflowItems.map((item, index) => (
                        <AvatarGroupItem
                          className={styles.avatarGroupItemAll}
                          key={item?.id}
                          image={{ src: item.image }}
                          name={item.name}
                          color={"anchor"}
                        />
                      ))}
                    </AvatarGroupPopover>
                  )}
                </AvatarGroup>
                <div className={styles.icons}>
                  {textChatEnabled && <ChatMultiple20Filled onClick={() => openChatUserAll()} />}
                  {canUpdateAllSettings && <Settings20Filled onClick={() => selectPerson()} />}
                </div>
              </div>
            </section>
          )}

          {/* Hosts Section*/}
          {userOwner.length !== 0 && (
            <>
              <Divider className={styles.divider} />
              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Caption1Strong className={styles.sectionHeaderText}>Hosts</Caption1Strong>
                </div>

                {userOwner.map((person, index) => {
                  return (
                    <PersonaParticipant
                      openChatUser={openChatUser}
                      canUpdateRoles={isMod}
                      person={person}
                      styles={styles}
                      onMute={onMute}
                      selectPerson={selectPerson}
                      key={`person-${person.id}-${index}`}
                    />
                  );
                })}
              </section>
            </>
          )}

          {/* Guests Section*/}
          {guestPeople.length !== 0 && (
            <>
              <Divider className={styles.divider} />
              <section className={styles.section}>
                <div className={styles.sectionHeader}>
                  <Caption1Strong className={styles.sectionHeaderText}>Guests</Caption1Strong>
                </div>

                {guestPeople.map(person => {
                  return (
                    <PersonaParticipant
                      openChatUser={openChatUser}
                      canUpdateRoles={isMod}
                      person={person}
                      onMute={onMute}
                      styles={styles}
                      selectPerson={selectPerson}
                      key={person.id}
                    />
                  );
                })}
              </section>
            </>
          )}
        </div>
      </DrawerBody>

      <div id="mount-chat"></div>
    </OverlayDrawer>
  );
}

function AIParticipant({ styles, agent, openChatUser }) {
  const textChatEnabled = !!configManager.config.textChat?.enabled;
  return (
    <div className={styles.sectionContent}>
      <Persona
        className={styles.persona}
        name={`${agent?.name}`}
        avatar={{
          color: "colorful"
        }}
      />
      <div className={styles.icons}>
        {textChatEnabled && <ChatMultiple20Filled onClick={() => openChatUser(agent)} />}
      </div>
    </div>
  );
}

function PersonaParticipant({ canUpdateRoles, styles, person, onMute, selectPerson, openChatUser }) {
  const { getUnreadMessagesCount, getLatestPrivateMessage, myMessages, selectedUserChat, readAllMesages } =
    useSocialContext();

  const latestMessage = getLatestPrivateMessage(person?.id);
  useEffect(() => {
    if (selectedUserChat) {
      readAllMesages(selectedUserChat.id);
    }
  }, [latestMessage]);

  const isMe = person.isMe;
  const voiceChatEnabled = configManager.config.voiceChat.enabled;

  function getVoiceIconComponent(micPresence) {
    if (micPresence && voiceChatEnabled) {
      if (micPresence.muted) {
        return MicOff20Filled;
      } else {
        return Mic20Filled;
      }
    }

    return MicOff20Filled;
  }

  const VoiceIcon = getVoiceIconComponent(person.micPresence);
  const getClassNameVoiceIcon = micPresence => {
    if (micPresence && voiceChatEnabled) {
      if (micPresence.muted === false) {
        return "";
      }
    }

    return styles.redIcon;
  };

  const classNameVoiceIcon = getClassNameVoiceIcon(person.micPresence);

  const textChatEnabled = !!configManager.config?.textChat?.enabled;

  return (
    <div className={styles.sectionContent}>
      <Persona
        className={styles.persona}
        name={`${person?.profile?.displayName} ${isMe ? "(You)" : ""}`}
        avatar={{
          image: {
            src: person?.profile?.thumbnail
          }
        }}
        presence={
          person?.presence != "lobby"
            ? {
                status: "available"
              }
            : {
                icon: <ClockFilled style={{ width: "12px", height: "12px" }} color="#EAA300" />
              }
        }
      />
      <div className={styles.icons}>
        {latestMessage && !latestMessage?.messages[0]?.read && (
          <CounterBadge count={getUnreadMessagesCount(person.id)} />
        )}
        {!isMe && textChatEnabled && <ChatMultiple20Filled onClick={() => openChatUser(person)} />}
        {(canUpdateRoles || isMe) && person?.presence != "lobby" && (
          <VoiceIcon
            style={!voiceChatEnabled ? { pointerEvents: "none" } : {}}
            className={classNameVoiceIcon}
            onClick={() => onMute(person)}
          />
        )}
        {<Settings20Filled onClick={() => selectPerson(person)} />}
      </div>
    </div>
  );
}
